import * as React from "react";
import { Grid, GridColumn as Column, GridNoRecords } from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Label } from "@progress/kendo-react-labels";
import { toast } from "react-toastify";
import SuccessModal from "components/common/Modal/success";

import * as api from "api/index";
import styles from "../Settings.module.scss";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Checkbox } from "@progress/kendo-react-inputs";

import EditForm from "./AddEditForm";
import { getActiveFeeEarner } from "services/userService"; // <-- use async fn instead of hook
import { GridLoader } from "components/GridLoader";

const Teams = () => {
  // ----------------------
  // State
  // ----------------------
  const [teamsData, setTeamsData] = React.useState([]);
  const [teamsValue, setTeamsValue] = React.useState(null);
  const [teamMembers, setTeamMembers] = React.useState([]);
  const [allFeeEarners, setAllFeeEarners] = React.useState([]);
  const [availableFeeEarners, setAvailableFeeEarners] = React.useState([]);

  const [isInactive, setIsInactive] = React.useState(false);

  const [openForm, setOpenForm] = React.useState(false);
  const [editItem, setEditItem] = React.useState(null);
  const [type, setType] = React.useState("");
  const [modalValue, setModalValue] = React.useState(false);
  const [addNewForm, setAddNewForm] = React.useState(false);

  // ----------------------
  // Loading states
  // ----------------------
  const [loadingTeams, setLoadingTeams] = React.useState(false);
  const [loadingMembers, setLoadingMembers] = React.useState(false);
  const [loadingFeeEarners, setLoadingFeeEarners] = React.useState(false);
  const [submitting, setSubmitting] = React.useState(false);

  // ----------------------
  // Grid state
  // ----------------------
  const [dataState, setDataState] = React.useState({
    take: 50,
    skip: 0,
  });

  // ----------------------
  // Helpers
  // ----------------------
  const refreshAvailableFeeEarners = (members = [], all = allFeeEarners) => {
    const memberIds = new Set(members.map((m) => m.userRef));
    const filtered = all.filter((f) => !memberIds.has(f.userRef));
    setAvailableFeeEarners(filtered.map((f) => ({ ...f, select: false })));
  };

  const checkInactive = (team) => {
    setIsInactive(team?.active === false);
  };

  // ----------------------
  // Fetchers
  // ----------------------
  const fetchFeeEarners = async () => {
    try {
      setLoadingFeeEarners(true);
      const res = await getActiveFeeEarner();
      const processed = (res || []).map((f) => ({
        ...f,
        select: false,
      }));
      setAllFeeEarners(processed);
      setAvailableFeeEarners(processed);
      return processed;
    } catch (err) {
      return [];
    } finally {
      setLoadingFeeEarners(false);
    }
  };

  const fetchTeams = async (feeEarnersLoaded = []) => {
    try {
      setLoadingTeams(true);
      const res = await api.getTeams();
      const data = res?.data?.data || [];
      setTeamsData(data);

      if (data.length > 0) {
        const firstTeam = data[0];
        setTeamsValue(firstTeam);
        checkInactive(firstTeam);
        fetchTeamMembers(firstTeam.teamID, firstTeam, feeEarnersLoaded);
      }
    } catch (err) {} finally {
      setLoadingTeams(false);
    }
  };

  const fetchTeamMembers = async (teamID, teamValue = teamsValue, feeEarners = allFeeEarners) => {
    try {
      setLoadingMembers(true);
      const res = await api.getTeamsById(teamID);
      const members = (res?.data?.data || []).map((m) => ({
        ...m,
        select: false,
      }));
      setTeamMembers(members);
      refreshAvailableFeeEarners(members, feeEarners);
      setTeamsValue(teamValue);
      checkInactive(teamValue);
    } catch (err) {} finally {
      setLoadingMembers(false);
    }
  };

  // ----------------------
  // Add/remove
  // ----------------------
  const updateTeamMembers = async (list, active) => {
    if (list.length === 0) {
      toast.error("Select Fee Earners");
      return;
    }

    const payload = list.map((i) => ({ ...i, active }));
    try {
      setSubmitting(true);
      await api.addRemoveTeamMembers(payload, teamsValue.teamID);
      toast.success("Team Updated");
      fetchTeamMembers(teamsValue.teamID, teamsValue);
    } catch (err) {} finally {
      setSubmitting(false);
    }
  };

  const addToTeam = () => {
    updateTeamMembers(availableFeeEarners.filter((i) => i.select), true);
  };

  const removeFromTeam = () => {
    updateTeamMembers(teamMembers.filter((i) => i.select), false);
  };

  // ----------------------
  // Form submit
  // ----------------------
  const handleSubmit = async (event) => {
    try {
      setSubmitting(true);
      if (type === "add") {
        if (!event.teamName || !event.branchId) return;
        await api.createNewTeam(event);
      } else if (type === "edit") {
        if (!event.teamName || !event.branchId) return;
        await api.editTeam(event, event.teamID);
      }
      setModalValue(true);
    } catch (err) {} finally {
      setSubmitting(false);
      setOpenForm(false);
    }
  };

  const handleCancelEdit = () => {
    setOpenForm(false);
  };

  // ----------------------
  // Effect
  // ----------------------
  React.useEffect(() => {
    (async () => {
      const feeEarners = await fetchFeeEarners();
      if (feeEarners.length > 0) {
        await fetchTeams(feeEarners);
      }
    })();
  }, []);

  // ----------------------
  // Custom cells
  // ----------------------
  const ActiveCell = (props) => (
    <td className={styles[`checkbox`]}>
      <Checkbox
        className="ms-2"
        checked={props.dataItem.select === true}
        onChange={(e) =>
          setAvailableFeeEarners((prev) =>
            prev.map((i) =>
              i.userID === props.dataItem.userID
                ? { ...i, select: e.value }
                : i
            )
          )
        }
      />
    </td>
  );

  const SelectionCell = (props) => (
    <td className={styles[`checkbox`]}>
      <Checkbox
        className="ms-2"
        checked={props.dataItem.select === true}
        onChange={(e) =>
          setTeamMembers((prev) =>
            prev.map((i) =>
              i.userRef === props.dataItem.userRef
                ? { ...i, select: e.value }
                : i
            )
          )
        }
      />
    </td>
  );

  // ----------------------
  // Render
  // ----------------------
  return (
    <div className="d-flex flex-column h-100 w-100 overflow-hidden">
      {!addNewForm && (
        <>
          <div className="d-flex justify-content-between align-items-center mb-3 flex-shrink-0">
            <div className="d-flex align-items-center">
              <div className="d-flex flex-column" style={{ height: "90px" }}>
                <Label className="label mb-2">Select Team</Label>
                <ComboBox
                  style={{ width: "350px" }}
                  data={teamsData}
                  loading={loadingTeams}
                  textField="teamName"
                  value={teamsValue}
                  onChange={(e) => {
                    if (e.value) {
                      fetchTeamMembers(e.value.teamID, e.value);
                    } else {
                      setTeamsValue(null);
                      setTeamMembers([]);
                      setAvailableFeeEarners(allFeeEarners);
                    }
                  }}
                  placeholder="Search Team"
                />
                {teamsValue?.branchName && (
                  <div
                    className="p-1 my-1 px-3"
                    style={{
                      backgroundColor: "#dcdaff",
                      color: "#262626",
                      fontSize: "12px",
                      borderRadius: "12px",
                    }}
                  >
                    <span className="fw-semibold" style={{ color: "#4843d3" }}>
                      Branch:{" "}
                    </span>
                    <span className="fw-semibold">{teamsValue.branchName}</span>
                  </div>
                )}
              </div>
              <div>
                <Label className="label ms-3 mt-4 pt-1 text-danger">
                  {isInactive && "Inactive"}
                </Label>
              </div>
            </div>
            <div className="d-flex flex-wrap gap-1 gap-xl-3">
              <Button
                className="common-btn"
                disabled={!teamsValue}
                onClick={() => {
                  setEditItem(teamsValue);
                  setType("edit");
                  setOpenForm(true);
                }}
              >
                Edit Team
              </Button>
              <Button
                className="common-btn"
                onClick={() => {
                  setType("add");
                  setOpenForm(true);
                }}
              >
                Add Team
              </Button>
            </div>
          </div>

          <div className="row flex-grow-1 w-100" style={{ minHeight: 0 }}>
            {/* Available */}
            <div className={`settings-grid users-grid col-5 h-100 ${styles[`teams-grid`]}`}>
              <Grid
                {...dataState}
                data={availableFeeEarners}
                style={{ height: "100%" }}
                sortable
                onDataStateChange={(e) => setDataState(e.dataState)}
                showLoader={loadingFeeEarners || loadingMembers}
                loader={
                  <div style={{ height: "50px" }}>
                    <GridLoader />
                  </div>
                }
              >
                <GridNoRecords>
                  {loadingFeeEarners || loadingMembers ? (
                    <span></span>
                  ) : (
                    availableFeeEarners && availableFeeEarners?.length === 0 && "No records available"
                  )}
                </GridNoRecords>
                <Column field="userRef" title="User Ref" width={100} />
                <Column field="displayname" title="User Name" />
                <Column field="select" title="Select" cell={ActiveCell} width={80} />
              </Grid>
            </div>

            {/* Buttons */}
            <div className="col-2 d-flex flex-column align-items-center justify-content-center">
              <Button
                className={`common-btn p-2 ms-3 ${styles[`button-1`]}`}
                disabled={submitting}
                onClick={addToTeam}
              >
                {">"}
              </Button>
              <Button
                className={`common-btn p-2 ms-3 mt-2 ${styles[`button-1`]}`}
                disabled={submitting}
                onClick={removeFromTeam}
              >
                {"<"}
              </Button>
            </div>

            {/* Members */}
            <div className={`settings-grid users-grid col-5 h-100 ${styles[`teams-grid`]}`}>
              <Grid
                {...dataState}
                data={teamMembers}
                style={{ height: "100%" }}
                sortable
                onDataStateChange={(e) => setDataState(e.dataState)}
                loading={loadingMembers}
              >
                <Column field="userRef" title="User Ref" width={100} />
                <Column field="displayname" title="User Name" />
                <Column field="select" title="Select" cell={SelectionCell} width={80} />
              </Grid>
            </div>
          </div>
        </>
      )}

      {/* Modals */}
      {modalValue && (
        <SuccessModal
          onClose={() => {
            fetchTeams(allFeeEarners);
            setModalValue(false);
          }}
          message={type === "add" ? "Team Added" : "Team Updated"}
        />
      )}

      {openForm && (
        <Dialog
          onClose={handleCancelEdit}
          width={600}
          title={type === "add" ? "Add Team" : "Edit Team"}
        >
          <div className="company">
            <EditForm
              data={teamsData}
              cancelEdit={handleCancelEdit}
              onSubmit={handleSubmit}
              item={editItem}
              type={type}
            />
          </div>
        </Dialog>
      )}
    </div>
  );
};

export default Teams;
